<template>
<div>
    <div class="card mb-3" :class="{'bg-success-subtle':backfillStatus?.summary?.allCompleted}">
      <div class="card-body">
        <h5 class="card-title">
          <i class="bi bi-database-fill-down me-2"></i>
          Historical Data Import
        </h5>
        <div v-if="!backfillStatus?.summary?.allCompleted" class="card-text text-muted mb-3">
            Import past 30 days of orders and transactions, plus all active inventory from your eBay store.
        </div>


        <!-- Show button when no backfill exists or all completed -->
        <div
            v-if="!backfillLoading && (!backfillStatus || !backfillStatus.status || backfillStatus.status.length === 0)"
            class="d-flex justify-content-between align-items-center gap-3"
        >
          <button
              type="button"
              class="btn"
              :class="{'btn-primary':!backfillStatus?.summary?.allCompleted, 'btn-link btn-sm':backfillStatus?.summary?.allCompleted}"
              @click="startBackfill"
          >
            <i class="bi bi-cloud-download me-2"></i>
            {{ backfillStatus?.summary?.allCompleted ? 'Re-import Historical Data' : 'Import Historical Data' }}
          </button>
          <button
              v-if="eshop.status === 'PENDING'"
              type="button"
              class="btn btn-link btn-sm"
              @click="skipBackfill"
          >Skip Backfill</button>
        </div>

        <!-- Show starting message immediately after button click -->
        <div v-else-if="backfillLoading" class="mt-3">
          <div class="alert alert-info d-flex align-items-center">
            <span class="spinner-border spinner-border-sm me-2"></span>
            <span>Starting historical data import...</span>
          </div>
        </div>

        <!-- Show progress when backfill is in progress OR show completion summary -->
        <div v-else-if="backfillStatus?.status && backfillStatus.status.length > 0" class="mt-3">
          <h6 class="mb-3">
            <span v-if="backfillStatus.summary?.inProgress">
              <i v-if="allQueued" class="bi bi-hourglass-split text-info me-2"></i>
              <i v-else class="bi bi-arrow-repeat text-primary me-2"></i>
              {{ allQueued ? 'Starting Import...' : 'Import in Progress...' }}
            </span>
            <span v-else-if="backfillStatus.summary?.allCompleted">
              <i class="bi bi-check-circle-fill text-success me-2"></i>
              Import Completed
            </span>
          </h6>

          <div v-if="backfillStatus.summary?.inProgress">
            <div v-for="item in backfillStatus.status" :key="item.backfillType" class="mb-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <strong class="text-capitalize">{{ item.backfillType }}</strong>
                <small class="text-muted">
                  <i v-if="item.status === 'completed'" class="bi bi-check-circle-fill text-success"></i>
                  <i v-else-if="item.status === 'in_progress'" class="bi bi-arrow-repeat text-primary"></i>
                  <i v-else-if="item.status === 'queued'" class="bi bi-hourglass-split text-info"></i>
                  <i v-else-if="item.status === 'failed'" class="bi bi-x-circle-fill text-danger"></i>
                  <i v-else class="bi bi-circle text-secondary"></i>
                  {{ item.status.replace('_', ' ') }}
                </small>
              </div>
              <div class="progress" style="height: 24px;">
                <div
                    class="progress-bar"
                    :class="{
                          'bg-success': item.status === 'completed',
                          'bg-danger': item.status === 'failed',
                          'bg-info progress-bar-striped progress-bar-animated': item.status === 'queued',
                          'progress-bar-striped progress-bar-animated': item.status === 'in_progress'
                        }"
                    :style="{ width: getProgressWidth(item) }"
                >
                  <strong>{{ getStatusMessage(item) }}</strong>
                </div>
              </div>
              <div v-if="item.status === 'failed' && item.errorMessage" class="alert alert-danger mt-2 mb-0">
                <small><strong>Error:</strong> {{ item.errorMessage }}</small>
              </div>
            </div>

            <div v-if="allQueued" class="alert alert-info d-flex gap-2 align-items-start mt-3">
              <i class="bi bi-info-circle-fill flex-shrink-0 mt-1"></i>
              <div>
                <strong>Import job is starting up.</strong>
                It typically takes 2&ndash;3 minutes before progress appears &mdash; this is normal.
                You can leave this page and come back; the import runs in the background.
              </div>
            </div>
            <div v-else class="alert alert-info d-flex gap-2 align-items-start mt-3">
              <i class="bi bi-info-circle-fill flex-shrink-0 mt-1"></i>
              <div>
                <strong>Inventory imports may take a while.</strong>
                Stores with hundreds or thousands of listings can take 15&ndash;30 minutes to fully import.
                You can continue using the app now &mdash; the import will keep running in the background.
              </div>
            </div>
          </div>

          <div v-if="backfillStatus.summary?.allCompleted" class="alert alert-success mb-3">
            <strong>Import Complete!</strong>
            <span v-if="backfillStatus.summary?.totalItemsProcessed > 0">
              Total items processed: {{ backfillStatus.summary?.totalItemsProcessed }}
            </span>
            <span v-else>
              No recent activity found - you're all set to start fresh!
            </span>
          </div>

          <div v-if="backfillStatus.summary?.allCompleted && eshop?.status !== 'ACTIVE'" class="text-center mb-3">
            <button
                type="button"
                class="btn btn-success btn-lg px-5"
                @click="getStarted"
            >
              <i class="bi bi-rocket-takeoff me-2"></i>
              Get Started
            </button>
          </div>

          <div class="d-flex justify-content-between gap-2 flex-wrap">
            <button
                v-if="backfillStatus.summary?.allCompleted"
                type="button"
                class="btn btn-link btn-sm"
                @click="startBackfill"
                :disabled="backfillLoading"
            >
              <i class="bi bi-arrow-repeat me-2"></i>
              Re-import Historical Data
            </button>

            <button
                v-if="backfillStatus.summary?.inProgress"
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @click="checkBackfillStatus"
            >
              <i class="bi bi-arrow-clockwise me-1"></i>
              Refresh Status
            </button>

            <button
                v-if="backfillStatus.summary?.inProgress && eshop.status === 'PENDING'"
                type="button"
                class="btn btn-sm btn-primary"
                @click="continueToApp"
                :disabled="continuingToApp"
            >
              <span v-if="continuingToApp" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-arrow-right-circle me-1"></i>
              Continue to Dashboard
            </button>
          </div>
        </div>

        <div v-if="backfillError" class="alert alert-danger mt-3">
          {{ backfillError }}
        </div>
      </div>
    </div>
  </div>

</template>

<script setup>
import {defineEmits, computed, ref, onMounted, onUnmounted} from 'vue'
import { useStore } from 'vuex'
const emits = defineEmits(['onError','onComplete','onUpdate','onGetStarted'])
const store = useStore()
const eshop = computed(() => store.state.eshop.eshop)

const backfillStatus = ref(null)
const backfillLoading = ref(false)
const backfillError = ref(null)
const activeBackfillStarted = ref(false)
const continuingToApp = ref(false)

// True when job is dispatched but container hasn't started yet (cold start window)
const allQueued = computed(() =>
  backfillStatus.value?.status?.length > 0 &&
  backfillStatus.value.status.every(s => s.status === 'queued' || s.status === 'pending')
)

// const eBayStatus = computed(() => store.state.eBayStatus)

let statusInterval = null

function startBackfill() {
  backfillLoading.value = true
  backfillError.value = null
  activeBackfillStarted.value = true

  store.dispatch('eshop/triggerBackfill')
      .then(() => {
        // Keep loading state while waiting for jobs to start
        // Will be cleared by first status check that shows in_progress
        checkBackfillStatus()
        startStatusPolling()
      })
      .catch(error => {
        backfillLoading.value = false
        backfillError.value = error.response?.data?.error || error.message || 'Failed to start import'
      })
}

function checkBackfillStatus() {
  if (!eshop.value?.id) return

  store.dispatch('eshop/getBackfillStatus')
      .then(status => {
        backfillStatus.value = status

        // Clear loading state once we have status
        if (status.summary?.inProgress || status.summary?.allCompleted) {
          backfillLoading.value = false
        }

        // Resume polling if a job is active but polling isn't running
        // (e.g. user navigated away and came back)
        if (status.summary?.inProgress && !statusInterval) {
          startStatusPolling()
        }

        // Only stop polling when all tasks are explicitly completed
        // Don't stop just because inProgress is false - jobs may still be starting
        if (status.summary?.allCompleted) {
          stopStatusPolling()
          // Only reload store and emit if WE started this backfill
          if (activeBackfillStarted.value) {
            activeBackfillStarted.value = false
            store.dispatch('eshop/loadStore')
            emits('onComplete')
          }
        }
      })
      .catch(error => {
        console.error('Error checking backfill status:', error)
        // Clear loading state on error
        backfillLoading.value = false
        // Don't show error if no backfill has been started yet
        if (error.response?.status !== 400) {
          backfillError.value = 'Failed to check status'
        }
      })
}

function startStatusPolling() {
  // Poll every 3 seconds while import is in progress
  if (statusInterval) clearInterval(statusInterval)
  statusInterval = setInterval(checkBackfillStatus, 3000)
}

function stopStatusPolling() {
  if (statusInterval) {
    clearInterval(statusInterval)
    statusInterval = null
  }
}

function getProgressWidth(item) {
  if (item.status === 'completed') return '100%'
  if (item.status === 'failed') return '100%'
  if (item.status === 'queued') return '100%'
  if (item.totalItems > 0) {
    const percentage = (item.processedItems / item.totalItems) * 100
    return `${Math.min(percentage, 100)}%`
  }
  // If we don't know total, show indeterminate progress
  if (item.status === 'in_progress') return '50%'
  return '0%'
}

function getStatusMessage(item) {
  const processed = item.processedItems || 0
  const total = item.totalItems || 0

  if (item.status === 'completed') {
    if (processed === 0) {
      const timeRange = item.backfillType === 'orders' ? '30 days' : '90 days'
      return `No items found (past ${timeRange})`
    }
    if (total > 0) {
      return `${processed} of ${total} items imported`
    }
    return `${processed} items imported`
  }

  if (item.status === 'in_progress') {
    if (total > 0) {
      return `${processed} of ${total} items...`
    }
    return `${processed} items processed...`
  }

  if (item.status === 'queued') {
    return 'Starting up...'
  }

  if (item.status === 'failed') {
    return 'Import failed - see error below'
  }

  return 'Waiting to start...'
}

async function skipBackfill() {
  const response = await store.state.api.post('/ebay/skip-backfill')
  const success = response.data.success
  if (success) {
    emits('onComplete')
  } else {
    alert(response.data.error?.message || response.data.error || 'Failed to skip')
  }
}

async function continueToApp() {
  continuingToApp.value = true
  try {
    const response = await store.state.api.post('/ebay/skip-backfill')
    const success = response.data.success
    if (success) {
      stopStatusPolling()
      emits('onComplete')
    } else {
      alert(response.data.error?.message || response.data.error || 'Failed to continue')
    }
  } finally {
    continuingToApp.value = false
  }
}

function getStarted() {
  emits('onGetStarted')
}


onMounted(() => {
  checkBackfillStatus()
})

onUnmounted(() => {
  if (statusInterval) {
    clearInterval(statusInterval)
  }
})

</script>



<style scoped lang="scss">

</style>